import { ReactNode, useEffect, useRef } from 'react';
import { View, Text, Image } from 'react-native';
import { SafeAreaView, type Edge } from 'react-native-safe-area-context';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { ChevronLeft, Bell } from 'lucide-react-native';
import type { Href } from 'expo-router';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useDesktopReturnSlot } from '@/components/screen-frame-slot';
import { useThemeColors } from '@/lib/useThemeColors';
import { usePhoneShell, useDesktopShell } from '@/lib/useShell';
import { isWeb } from '@/lib/platform';
import { useAuth } from '@/lib/supabase';
import { useRealtimeNotifications } from '@/lib/supabase/realtime';
import { useProfileStore } from '@/lib/state/profile-store';
import { useIsAdmin } from '@/lib/auth/role';

/**
 * ScreenFrame — the ONE mobile page-header contract.
 *
 * WHY THIS EXISTS. There was no page-header component anywhere in this app.
 * Every screen hand-rolled `{!isWeb && <Animated.View className="px-5 pt-4 …">}`,
 * so comparable pages independently chose their own safe-area edges, header
 * spacing, scroll placement and return behaviour — and roughly a third of them
 * chose nothing at all for the top inset. `ScreenWrapper` was the intended
 * contract but both branches of its padding ternary were the empty string and it
 * had zero importers; it has been deleted rather than left as a second unused
 * wrapper.
 *
 * WHAT IT OWNS (and what a screen therefore stops deciding):
 *   - safe-area edges. Declared once, here. Top is always claimed on a phone
 *     shell; bottom is opt-in via `bottomEdge` for screens with no tab bar
 *     beneath them. Under the desktop shell it claims NOTHING, because
 *     `WebAppShell` already supplies the chrome — that is how double-insetting
 *     is prevented without a blanket wrapper.
 *   - the header row: leading Back/Exit, title, trailing actions.
 *   - horizontal alignment (the existing px-5 content origin).
 *
 * WHAT IT DELIBERATELY DOES NOT OWN: the body. Screens keep their own scroll
 * views, animations and content. This is a frame, not a template.
 *
 * PHONE WEB. The header renders whenever the PHONE shell is active — native and
 * phone web alike — replacing the `{!isWeb && …}` idiom that blanked page
 * identity in a browser at 390px. Above the tablet breakpoint it yields to the
 * sidebar's title and breadcrumbs so the two never both draw a header.
 */

export interface ScreenFrameBack {
  /**
   * REQUIRED. Where to go when there is no history to pop — a deep link, a web
   * refresh, or the first screen of a fresh stack. `router.back()` on an empty
   * history is a dead control, which is exactly what the valuation wizard's
   * step-zero arrow was. A fallback makes direct entry indistinguishable from
   * in-app entry, so there is no way to author a back button that can strand
   * someone.
   */
  fallback: Href;
  /** Compact parent label. Omitted on phones by default — a long breadcrumb
   *  trail does not belong on a 320pt row — but available where the parent is
   *  not obvious from context. */
  label?: string;
  /** Intercept the press (e.g. to confirm discarding a draft). Return `false`
   *  to cancel the navigation; anything else lets it proceed. */
  onPress?: () => boolean | void;
}

interface ScreenFrameProps {
  children: ReactNode;
  /** Page identity. Shown on the phone shell; the sidebar shows it on desktop. */
  title?: string;
  /** Small line above the title (e.g. a company name, a step count). */
  eyebrow?: string;
  /** Present on SECONDARY pages; omit on primary destinations that have a tab. */
  back?: ScreenFrameBack;
  /** Trailing controls. Global utilities on primary pages
   *  (`<ScreenFrame.GlobalActions />`), contextual actions on secondary ones. */
  actions?: ReactNode;
  /**
   * Claim the bottom inset too.
   *
   * ONLY for routes with no tab bar beneath them — i.e. registered on the ROOT
   * stack, not as a `Tabs.Screen`. A `href: null` tab screen is still a tab
   * screen: it is hidden from the bar, it does not escape it. The valuation
   * wizard set this while sitting inside the startup Tabs navigator and rendered
   * a visible empty band between its footer and the tab bar.
   *
   * If you are about to pass this, check the route's registration first.
   */
  bottomEdge?: boolean;
  /** Background for the frame. Defaults to the app's secondary surface. */
  backgroundColor?: string;
}

export function ScreenFrame({
  children,
  title,
  eyebrow,
  back,
  actions,
  bottomEdge = false,
  backgroundColor,
}: ScreenFrameProps) {
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const router = useRouter();

  // One owner per edge. Under the desktop shell we claim none — WebAppShell is
  // the owner there. On a phone shell (native OR installed PWA) we claim top,
  // and bottom only when asked. In an ordinary desktop browser tab the CSS
  // env(safe-area-inset-*) values are zero, so this is a no-op rather than a
  // wrong guess about a platform.
  const edges: Edge[] = phoneShell
    ? bottomEdge
      ? ['top', 'bottom']
      : ['top']
    : [];

  const handleBack = () => {
    if (back?.onPress?.() === false) return;
    if (!isWeb) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    // canGoBack() is the whole point of the fallback contract: direct entry has
    // no history to pop, and replace() (not push) keeps the stack honest.
    if (router.canGoBack()) router.back();
    else router.replace(back!.fallback);
  };

  // DESKTOP GETS THE SAME EXIT, THROUGH THE SHELL'S HEADER.
  // Drawing this row on desktop would duplicate the sidebar's title and
  // breadcrumbs, but suppressing it entirely left the flow with no return and —
  // for the wizard — no guarded exit, since the sidebar navigates elsewhere
  // rather than leaving the flow. Registering it in the shell's header slot
  // keeps ONE definition of how you leave this screen: `handleBack` runs on both
  // shells, so the unsaved-changes prompt behaves identically.
  const desktopShell = useDesktopShell();
  const slot = useDesktopReturnSlot();
  // Read through a ref: `handleBack` is a new closure every render, and the
  // descriptor must stay stable or registering it would loop.
  const handleBackRef = useRef(handleBack);
  handleBackRef.current = handleBack;
  const backLabel = back?.label;
  const backFallback = back?.fallback;
  const hasBack = !!back;

  useEffect(() => {
    if (!slot || !desktopShell || !hasBack) return;
    slot.setReturn({
      label: backLabel ? `Back to ${backLabel}` : 'Back',
      run: () => handleBackRef.current(),
    });
    return () => slot.setReturn(null);
  }, [slot, desktopShell, hasBack, backLabel, backFallback]);

  const showHeader = phoneShell && (!!title || !!back || !!actions);

  return (
    <SafeAreaView
      edges={edges}
      style={{ flex: 1, backgroundColor: backgroundColor ?? colors.surface.secondary }}
    >
      {showHeader && (
        <Animated.View
          entering={FadeInDown.duration(300)}
          className="flex-row items-center gap-2 px-5 pt-2 pb-3"
        >
          {back && (
            // shrink-0: a long title must truncate rather than squeeze the
            // control below the 44pt touch target.
            <View className="shrink-0">
              <AnimatedPressable
                onPress={handleBack}
                accessibilityRole="button"
                accessibilityLabel={back.label ? `Back to ${back.label}` : 'Back'}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  backgroundColor: colors.surface.primary,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChevronLeft size={22} color={colors.text.primary} />
              </AnimatedPressable>
            </View>
          )}

          <View className="flex-1">
            {!!eyebrow && (
              <Text
                numberOfLines={1}
                className="text-xs font-semibold uppercase tracking-wider"
                style={{ color: colors.text.tertiary }}
              >
                {eyebrow}
              </Text>
            )}
            {!!title && (
              // numberOfLines + flex-1 parent: the title truncates. Large text
              // settings grow the row's height rather than clipping the glyphs,
              // because nothing here pins a height.
              <Text
                numberOfLines={1}
                accessibilityRole="header"
                className="text-2xl font-bold"
                style={{ color: colors.text.primary }}
              >
                {title}
              </Text>
            )}
          </View>

          {!!actions && (
            <View className="flex-row items-center gap-2 shrink-0">{actions}</View>
          )}
        </Animated.View>
      )}

      {children}
    </SafeAreaView>
  );
}

/**
 * The two GLOBAL utilities, in one fixed order for both personas: Notifications,
 * then the account avatar. Same controls, same place, same behaviour whichever
 * persona you are — which is what "consistent" means here. It does NOT mean the
 * two personas get identical destinations; their tab bars stay different because
 * their jobs are.
 *
 * The avatar opens the EXISTING role Profile route. It is not a second account
 * menu: settings, security, gated billing, help and sign out keep living inside
 * that Profile/settings hierarchy, reached from one place.
 */
function GlobalActions() {
  const colors = useThemeColors();
  const router = useRouter();
  const { user: authUser } = useAuth();
  const { unreadCount } = useRealtimeNotifications(authUser?.id);

  const personaRole = useProfileStore((s) => s.userRole);
  const storeUser = useProfileStore((s) => s.user);
  // Server-resolved authority, never the store cache — admin_roles is the sole
  // source of administrative authority, and persona is persona.
  const { isAdmin } = useIsAdmin(authUser?.id);

  const profileHref: Href = isAdmin
    ? '/settings'
    : personaRole === 'investor'
      ? '/(investor)/investorprofile'
      : '/(startup)/userprofile';

  const displayName = storeUser?.name ?? '';
  const initial = displayName.trim().charAt(0).toUpperCase() || '?';

  return (
    // OWNS ITS OWN GROUPING, deliberately. This used to return a bare fragment,
    // which made the two buttons separate children of whatever row the caller
    // happened to have. `startup-dashboard` had a `justify-between` row, so the
    // bell was distributed into the MIDDLE of the header and only the avatar sat
    // at the trailing edge — visibly wrong, and invisible to any prop-level test.
    // A control cluster that can be taken apart by its container is not a shared
    // component. `shrink-0` keeps both 44pt targets whatever the title does.
    <View className="flex-row items-center gap-2 shrink-0">
      <AnimatedPressable
        onPress={() => {
          if (!isWeb) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
          router.push('/notifications');
        }}
        accessibilityRole="button"
        // The count belongs in the accessible name — a bare "Notifications"
        // hides exactly the information the badge exists to convey.
        accessibilityLabel={
          unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'
        }
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          backgroundColor: colors.surface.primary,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Bell size={20} color={colors.text.secondary} />
        {unreadCount > 0 && (
          <View
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              width: 10,
              height: 10,
              borderRadius: 5,
              backgroundColor: colors.error,
              borderWidth: 2,
              borderColor: colors.surface.primary,
            }}
          />
        )}
      </AnimatedPressable>

      <AnimatedPressable
        onPress={() => {
          if (!isWeb) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
          router.push(profileHref);
        }}
        accessibilityRole="button"
        accessibilityLabel={displayName ? `Account, ${displayName}` : 'Account'}
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.primary[100],
          borderWidth: 2,
          borderColor: colors.primary[500],
        }}
      >
        {storeUser?.avatarUrl ? (
          <Image
            source={{ uri: storeUser.avatarUrl }}
            style={{ width: '100%', height: '100%' }}
            resizeMode="cover"
          />
        ) : (
          <Text className="text-base font-semibold" style={{ color: colors.primary[600] }}>
            {initial}
          </Text>
        )}
      </AnimatedPressable>
    </View>
  );
}

ScreenFrame.GlobalActions = GlobalActions;
